/* eslint-disable object-curly-newline */
import React, { useState, useEffect } from 'react';
import ReactTooltip from 'react-tooltip';
import { connect } from 'react-redux';
import Select from 'react-select';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import _flatten from 'lodash/flatten';
import _map from 'lodash/map';
import _isEmpty from 'lodash/isEmpty';
import FontOptions from './FontOptions';
import FormatOptionLabel from './FormatOptionLabel';
import * as Styled from './styles';
import { DropdownIndicator, generateSelectStyles } from '../../common/generateSelectStyling';

const FontSelect = ({
    hasFullStylingAccess,
    hasCustomFontsAccess,
    hasIntegrationCustomFontsAccess,
    customizationPrimaryFont,
    customizationSecondaryFont,
    isCustomFontsLoaded,
    customFonts,
    type,
    width,
    value,
    fontID,
    onChange,
    icon,
    marginBottom,
    styling,
    label,
}) => {
    const [fontOptions, setfontOptions] = useState(null);
    const [isCustomFontsInFontOptions, setIsCustomFontsInFontOptions] = useState(false);
    const [placeholder, setPlaceholder] = useState(value);

    useEffect(() => {
        setfontOptions(
            new FontOptions({
                hasFullStylingAccess,
                hasIntegrationCustomFontsAccess,
                customizationPrimaryFont,
                customizationSecondaryFont,
                customFonts,
            })
        );
    }, [
        hasFullStylingAccess,
        hasIntegrationCustomFontsAccess,
        customizationPrimaryFont,
        customizationSecondaryFont,
        customFonts,
    ]);

    useEffect(() => {
        if (
            !isCustomFontsInFontOptions &&
            fontOptions &&
            hasCustomFontsAccess &&
            isCustomFontsLoaded &&
            !_isEmpty(customFonts)
        ) {
            setIsCustomFontsInFontOptions(true);
            fontOptions.setCustomFonts(customFonts);
        }
    }, [hasCustomFontsAccess, isCustomFontsLoaded, fontOptions, customFonts, isCustomFontsInFontOptions]);

    // we are not putting onChange into the deps array because its calling it many times and not only once
    useEffect(() => {
        if (fontOptions && !_isEmpty(fontOptions.values) && type === 'fontFamily') {
            const allFontsFlat = _flatten(_map(fontOptions.values[type], ({ options }) => options));

            // if we are able to find the font by value, just update the FontSelect value
            const fontFamilyByName = allFontsFlat.find((e) => e.value === value);
            if (fontFamilyByName) {
                setPlaceholder(fontFamilyByName.label);
                return;
            }

            /* if we find the font by id, we need to update the FontSelelect value and also
             * update the font value in the blocks by using onChange, this way if there's a
             * change in the database, the element will be updated on select. */
            const fontFamilyByID = allFontsFlat.find((e) => e.fontID === fontID);
            if (fontFamilyByID) {
                setPlaceholder(fontFamilyByID.label);
                // update the element value based on fontID
                onChange(fontFamilyByID);
                return;
            }

            setPlaceholder('Font not available');
        } else {
            setPlaceholder(value);
        }
        /* keep isCustomFontsLoaded in the dependency list so when the custom fonts are loaded the font selector
         * changes from 'Font not available' to the font name without requiring any user action, e.g. auto refresh */
    }, [fontOptions, type, value, fontID, isCustomFontsLoaded]); // eslint-disable-line

    return (
        <div
            style={{
                width,
                height: 30,
                marginBottom,
                border: '1px solid #CCCCCC',
                boxSizing: 'border-box',
                ...styling,
            }}
            data-test-id="font-select"
        >
            <Styled.FontSelectBoxDiv>
                {!!icon && (
                    <>
                        <Styled.FontSelectIconDiv>
                            <Styled.FontSelectIcon data-tip data-for={icon} className="material-icons">
                                {icon}
                            </Styled.FontSelectIcon>
                        </Styled.FontSelectIconDiv>
                        <ReactTooltip id={icon} place="top" type="dark">
                            <Styled.FontSelectBoldSpan>{label}</Styled.FontSelectBoldSpan>
                        </ReactTooltip>
                    </>
                )}
                <Select
                    value={value}
                    placeholder={placeholder}
                    components={{ DropdownIndicator }}
                    formatOptionLabel={FormatOptionLabel}
                    onChange={onChange}
                    options={fontOptions ? fontOptions.values[type] : []}
                    isOptionDisabled={(option) => option.isDisabled}
                    styles={generateSelectStyles({ hideBorder: true, width })}
                />
            </Styled.FontSelectBoxDiv>
        </div>
    );
};

const mapStateToProps = (state) => ({
    // if EMAIL_DESIGNER_STYLING_ACCESS does not exist, we assume the user has full styling access
    hasFullStylingAccess: _get(state, 'session.user.features.EMAIL_DESIGNER_STYLING_ACCESS', true),
    hasCustomFontsAccess: _get(state, 'session.user.features.EMAIL_DESIGNER_CUSTOM_FONTS', false),
    hasIntegrationCustomFontsAccess: _get(state, 'session.features.F_CUSTOM_FONTS', false),
    customizationPrimaryFont: _get(state, 'session.user.customization.primaryFont', 'Arial'),
    customizationSecondaryFont: _get(state, 'session.user.customization.secondaryFont', 'Georgia'),
    isCustomFontsLoaded: _get(state, 'customFonts.isLoaded', false),
    customFonts: _get(state, 'customFonts.data', {}),
});

FontSelect.propTypes = {
    hasFullStylingAccess: PropTypes.bool.isRequired,
    hasCustomFontsAccess: PropTypes.bool.isRequired,
    hasIntegrationCustomFontsAccess: PropTypes.bool.isRequired,
    customizationPrimaryFont: PropTypes.string.isRequired,
    customizationSecondaryFont: PropTypes.string.isRequired,
    isCustomFontsLoaded: PropTypes.bool.isRequired,
    customFonts: PropTypes.object.isRequired,
    type: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
    value: PropTypes.string.isRequired,
    fontID: PropTypes.number,
    onChange: PropTypes.func.isRequired,
    icon: PropTypes.string,
    marginBottom: PropTypes.number,
    styling: PropTypes.object,
    label: PropTypes.string,
};

FontSelect.defaultProps = {
    icon: '',
    label: '',
    marginBottom: 9,
    styling: {},
    fontID: -1,
};

export default connect(mapStateToProps)(FontSelect);
